'use client';

import { mapInlineOption, normalizeExternalOptions } from '@/components/Form/Fields/shared/normalizeOptions';
import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import type { FormValues } from '@/components/Form/types';
import Label from '@/components/Label/Label';
import { FormControl, FormHelperText, FormLabel } from '@/components/field';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { RichText } from '@/components/richText/RichText';
import { SwdCheckbox } from '@/components/swdCheckbox/SwdCheckbox';
import { SwdFormControlLabel } from '@/components/swdFormControlLabel/SwdFormControlLabel';
import { SwdFormGroup } from '@/components/swdFormGroup/SwdFormGroup';
import { hasContent, richtextToString } from '@/helper/RichText';
import type {
  DatasourceItemStoryblok,
  FormCheckboxStoryblok,
  OptionItemStoryblok,
  RichtextStoryblok,
} from '@/types/component-types-sb';
import { type FieldInputProps, useFormikContext } from 'formik';
import { type FC, useEffect, useMemo, useState } from 'react';

interface ICheckboxField extends Omit<FormCheckboxStoryblok, 'label'>, Omit<IDefaultFormBuilderProps, 'label'> {
  field: FieldInputProps<FormValues>;
  label?: RichtextStoryblok | string;
}

// UI representation for options (accepts string or richtext label)
interface UIOption {
  _uid?: string;
  value: string;
  label: string | RichtextStoryblok;
}

const CheckboxField: FC<ICheckboxField> = (props) => {
  const { values, handleBlur, setFieldValue } = useFormikContext<FormValues>();
  const stateKey = props.field.name;

  const [dynamicOptions, setDynamicOptions] = useState<UIOption[] | null>(null);

  const [isLoadingDynamicOptions, setIsLoadingDynamicOptions] = useState<boolean>(false);

  // Build static options (only inline OptionItem entries)
  const staticOptions: UIOption[] = useMemo(() => {
    const src = Array.isArray(props.options) ? props.options : [];
    return src
      .filter((opt): opt is OptionItemStoryblok => (opt as OptionItemStoryblok).component === 'OptionItem')
      .map(mapInlineOption);
  }, [props.options]);

  // Expand options: inline OptionItem and dynamic DatasourceItem(s) in order
  useEffect(() => {
    let cancelled = false;

    const expand = async () => {
      const src = Array.isArray(props.options) ? props.options : [];

      // If no datasource items, we fall back to static options
      const hasDatasourceItems = src.some((opt) => (opt as DatasourceItemStoryblok).component === 'DatasourceItem');
      if (!hasDatasourceItems) {
        if (!cancelled) setDynamicOptions(null);
        return;
      }

      const results: UIOption[] = [];
      for (const item of src) {
        if ((item as OptionItemStoryblok).component === 'OptionItem') {
          results.push(mapInlineOption(item as OptionItemStoryblok));
          continue;
        }
        if ((item as DatasourceItemStoryblok).component === 'DatasourceItem') {
          const url = (item as DatasourceItemStoryblok).datasource?.trim();
          if (!url) continue;
          try {
            setIsLoadingDynamicOptions(true);
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) throw new Error(`Failed to load datasource ${url}: ${res.status}`);
            const json = (await res.json()) as unknown;
            const mapped = normalizeExternalOptions(json);
            if (mapped.length > 0) {
              results.push(...mapped);
            }
          } catch (_e) {
            // On error, skip this external datasource but continue processing others
          } finally {
            setIsLoadingDynamicOptions(false);
          }
        }
      }

      if (!cancelled) setDynamicOptions(results);
    };

    void expand();
    return () => {
      cancelled = true;
    };
  }, [props.options]);

  const renderOptions = dynamicOptions ?? staticOptions;
  const labelText = props.label ? richtextToString(props.label).trim() : '';
  const hasLabel = labelText.length > 0;
  const hasInfoText = hasContent(props.infoText);
  const accessibleLabel = [labelText, props.required ? 'Pflichtfeld' : ''].filter(Boolean).join(', ') || undefined;

  const getLabelForValue = (val: string): string => {
    const opt = renderOptions.find((o) => String(o.value) === String(val));
    if (!opt) return String(val);
    // Normalize label which may be string or RichText
    return typeof opt.label === 'string' ? opt.label : richtextToString(opt.label);
  };

  const handleCheckboxChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { checked, value } = e.target;
    const currentValues = Array.isArray(values[stateKey]) ? (values[stateKey] as string[]) : [];
    const nextValues = checked
      ? Array.from(new Set([...currentValues, value]))
      : currentValues.filter((v) => String(v) !== String(value));

    // Update the checkbox array explicitly so Formik validates against the complete next value.
    void setFieldValue(stateKey, nextValues);

    // Derive labels from nextValues to keep them in sync and ordered
    const nextLabels = nextValues.map((v) => getLabelForValue(String(v)));
    void setFieldValue(`${stateKey}__label`, nextLabels, false);
  };

  return (
    <FormControl
      error={props.isInvalid}
      required={props.required}
      aria-invalid={props.isInvalid || undefined}
      aria-label={accessibleLabel}
    >
      {(hasLabel || hasInfoText) && (
        <FormLabel required={props.required && hasLabel}>
          {hasLabel && (
            <>
              <Label text={props.label} />{' '}
            </>
          )}
          {hasInfoText && (
            <>
              <InfoIconModal trackingLabel={labelText}>
                <RichText text={props.infoText} />
              </InfoIconModal>{' '}
            </>
          )}
        </FormLabel>
      )}
      <LoadingBackdrop loading={isLoadingDynamicOptions} type="card" text="Daten werden geladen..." />
      {!isLoadingDynamicOptions && (
        <SwdFormGroup className="fullWidth" row={props.alignment === 'horizontal'}>
          {renderOptions.map((option) => {
            const value = values[stateKey];
            const isChecked = Array.isArray(value) ? value.includes(option.value) : false;
            return (
              <SwdFormControlLabel
                key={option._uid || option.value}
                error={props.isInvalid}
                control={
                  <SwdCheckbox
                    {...props.field}
                    onChange={handleCheckboxChange}
                    onBlur={handleBlur}
                    checked={isChecked}
                    id={props.id}
                    value={String(option.value)}
                    disabled={props.disabled}
                    data-testid="form-builder-checkbox"
                  />
                }
                label={<Label text={option.label} />}
              />
            );
          })}
        </SwdFormGroup>
      )}
      {props.isInvalid && props.errorMessage && <FormHelperText>{props.errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default CheckboxField;
